import { EntryType, type EntryItemType } from './entry-type';
import type {
  TreeBooleanItem,
  BaseTreeNode,
  TreeCellItem,
  TreeCodeItem,
  TreeGraphItem,
  TreeInternalItem,
  TreeMap,
  TreeSimpleItem,
  UnregisteredTreeItem,
} from '../tree';
import { match } from 'ts-pattern';
import { z } from 'zod';

/*
 * Types
 */

export enum SchemaLiteralType {
  Function = '#',
  Variable = '$',
}

export type CreateEntryItem = {
  key: string;
  value?: any;
  name?: string;
  parentId: string;
  validation?: z.ZodType<any, any, any>;
  allowedTypes?: EntryItemType[];
  semantics?: string;
  updateObject?: {
    type?: string;
    class?: string;
    dropdownid?: number;
    fieldtype?: string;
    enabled?: boolean;
  };
};

export type CreateMapItem = {
  key: string;
  name: string;
  parentId: string | undefined;
  value?: any;
  computed?: any;
  // FIXME: This should be StructureBlockIndex
  formerOutputType: 'unwrap' | 'group' | 'array' | 'forEach' | 'data' | 'code';
  outputType: 'unwrap' | 'group' | 'array' | 'forEach' | 'data' | 'code';
};

// TODO: maybe we can place this in the function below?
export const createUnregisteredMap = (
  partial: CreateMapItem,
): Omit<TreeMap, 'id'> => {
  return {
    ...partial,
    children: {
      forwardMap: {},
      reverseMap: {},
    },
    validation: z.any(),
    type: EntryType.Map,
    dependencies: {
      dependsOn: new Set(),
      requiredBy: new Set(),
    },
  };
};

const createBooleanItem = (
  value: boolean,
): UnregisteredTreeItem<TreeBooleanItem> => {
  return {
    type: EntryType.Boolean,
    value,
  };
};

const createSimpleItem = (value: any): UnregisteredTreeItem<TreeSimpleItem> => {
  return {
    type: EntryType.Simple,
    value,
  };
};

const createCellItem = (): UnregisteredTreeItem<TreeCellItem> => {
  return {
    type: EntryType.Cell,
    column: undefined,
    value: undefined,
  };
};

const createGraphItem = (): UnregisteredTreeItem<TreeGraphItem> => {
  return {
    type: EntryType.Graph,
    graphId: undefined,
    value: undefined,
  };
};

const createCodeItem = (value: any): UnregisteredTreeItem<TreeCodeItem> => {
  return {
    type: EntryType.Code,
    value,
  };
};

const createInternalItem = (
  value: any,
): UnregisteredTreeItem<TreeInternalItem> => {
  return {
    type: EntryType.Internal,
    value,
  };
};

export const createUnregisteredItem = (
  type: EntryItemType,
  {
    name,
    parentId,
    validation,
    key,
    value,
    allowedTypes,
    semantics,
    updateObject,
  }: CreateEntryItem,
) => {
  const base: Omit<BaseTreeNode, 'id'> = {
    key,
    name: name ?? key,
    parentId,
    computed: undefined,
    validation: validation ?? z.any(),
    allowedTypes,
    dependencies: {
      dependsOn: new Set(),
      requiredBy: new Set(),
    },
    semantics,
    updateObject,
  };

  return {
    ...base,
    ...match(type)
      .with(EntryType.Boolean, () => createBooleanItem(value))
      .with(EntryType.Simple, () => createSimpleItem(value))
      .with(EntryType.Cell, () => createCellItem())
      .with(EntryType.Graph, () => createGraphItem())
      .with(EntryType.Code, () => createCodeItem(value))
      .with(EntryType.Internal, () => {
        if (validation instanceof z.ZodLiteral) {
          value = value ?? validation.value;
        }

        return createInternalItem(value);
      })
      .exhaustive(),
  };
};
